import { AjaxHandler, ExtractTextOption } from '../index';
import { createElement, isNullOrUndefined, Browser } from '@syncfusion/ej2-base';
import { TaskPriorityLevel } from '../base/pdfviewer-utlis';
/**
 * The `ThumbnailView` module is used to handle thumbnail view navigation of PDF viewer.
 *
 * @param {Event} event - args
 * @returns {void}
 */
export class ThumbnailView {
    /**
     * @param {PdfViewer} pdfViewer - It describes about the pdfviewer
     * @param {PdfViewerBase} pdfViewerBase - It describes about the pdfviewer base
     * @private
     * @returns {void}
     */
    constructor(pdfViewer, pdfViewerBase) {
        this.thumbnailLimit = 30;
        this.thumbnailThreshold = 5;
        this.thumbnailRequestsBatch = 5;
        this.thumbnailTopMargin = 10;
        this.thumbnailTop = 8;
        this.isRendered = false;
        this.list = [];
        /**
         * @private
         */
        this.thumbnailPageSize = [];
        /**
         * @private
         */
        this.isThubmnailOpen = false;
        /**
         * @private
         */
        this.isThumbnailClicked = false;
        this.thumbnailOnScroll = (event) => {
            for (let i = 0; i < this.thumbnailPageSize.length; i++) {
                const scrollPosition = this.pdfViewerBase.navigationPane.sideBarContent.scrollTop;
                const index = this.thumbnailPageSize.findIndex((page) => page.top >= scrollPosition);
                if (index !== -1) {
                    const number = Math.floor((index) / this.thumbnailRequestsBatch) * this.thumbnailRequestsBatch;
                    this.updateScrollTopForThumbnail(number);
                    break;
                }
            }
        };
        /**
         * @param {MouseEvent} event - It describes about the event
         * @param {boolean} isKeyboard - It describes about the isKeyboard value
         * @private
         * @returns {void}
         */
        this.thumbnailClick = (event, isKeyboard) => {
            // eslint-disable-next-line
            const proxy = this;
            const target = event.target;
            const pageNumber = proxy.getPageNumberFromID(target.id);
            if (proxy.previousElement) {
                proxy.previousElement.classList.remove('e-pv-thumbnail-selection');
                proxy.previousElement.classList.remove('e-pv-thumbnail-focus');
                proxy.previousElement.classList.add('e-pv-thumbnail-selection-ring');
            }
            if (target.parentElement.id === proxy.pdfViewer.element.id + '_thumbnail_Selection_Ring_' + pageNumber) {
                proxy.setSelectionStyle(target.parentElement);
                proxy.previousElement = target.parentElement;
            }
            else if (target.id === proxy.pdfViewer.element.id + '_thumbnail_Selection_Ring_' + pageNumber) {
                proxy.setSelectionStyle(target);
                proxy.previousElement = target;
            }
            proxy.pdfViewer.fireThumbnailClick(pageNumber + 1);
            proxy.isThumbnailClicked = true;
            proxy.goToThumbnailPage(pageNumber + 1);
            if (!isKeyboard) {
                proxy.pdfViewerBase.focusViewerContainer();
            }
            if (this.pdfViewer.annotationModule && this.pdfViewer.annotationModule.inkAnnotationModule) {
                const currentPageNumber = parseInt(this.pdfViewer.annotationModule.inkAnnotationModule.currentPageNumber, 10);
                this.pdfViewer.annotationModule.inkAnnotationModule.drawInkAnnotation(currentPageNumber);
            }
        };
        /**
         * @param {KeyboardEvent} event - It describes about the event
         * @private
         * @returns {void}
         */
        this.thumbnailKeydown = (event) => {
            if (event && event.key === 'Enter' || event.key === ' ') {
                this.thumbnailClick(event, true);
                event.preventDefault();
                event.stopPropagation();
            }
        };
        /**
         * @param {MouseEvent} event - It describes about the event
         * @private
         * @returns {void}
         */
        this.thumbnailMouseOver = (event) => {
            // eslint-disable-next-line
            const proxy = this;
            const target = event.target;
            const pageNumber = proxy.getPageNumberFromID(target.id);
            if (target.id === proxy.pdfViewer.element.id + '_thumbnail_Selection_Ring_' + pageNumber) {
                proxy.setMouseOverStyle(target);
            }
            else if (target.id === proxy.pdfViewer.element.id + '_thumbnail_image_' + pageNumber) {
                proxy.setMouseOverStyle(target.parentElement);
            }
        };
        /**
         * @param {MouseEvent} event - It describes about the event
         * @private
         * @returns {void}
         */
        this.thumbnailMouseLeave = (event) => {
            // eslint-disable-next-line
            const proxy = this;
            const target = event.target;
            const pageNumber = proxy.getPageNumberFromID(target.id);
            if (target.parentElement.id === proxy.pdfViewer.element.id + '_thumbnail_view') {
                proxy.setMouseLeaveStyle(target.children[0].children[0]);
            }
            else if (target.parentElement.id === proxy.pdfViewer.element.id + '_thumbnail_' + pageNumber) {
                proxy.setMouseLeaveStyle(target.parentElement.children[0]);
            }
        };
        this.pdfViewer = pdfViewer;
        this.pdfViewerBase = pdfViewerBase;
    }
    /**
     * @private
     * @returns {void}
     */
    createThumbnailContainer() {
        this.thumbnailView = createElement('div', { id: this.pdfViewer.element.id + '_thumbnail_view', className: 'e-pv-thumbnail-view e-pv-thumbnail-row' });
        this.pdfViewerBase.navigationPane.sideBarContent.appendChild(this.thumbnailView);
        this.pdfViewerBase.navigationPane.sideBarContent.addEventListener('scrollend', this.thumbnailOnScroll);
    }
    /**
     * Open the thumbnail pane of the PdfViewer.
     *
     * @returns {void}
     */
    openThumbnailPane() {
        if (this.pdfViewerBase.navigationPane) {
            this.pdfViewerBase.navigationPane.openThumbnailPane();
        }
    }
    /**
     * Close the thumbnail pane of the PdfViewer.
     *
     * @returns {void}
     */
    closeThumbnailPane() {
        if (this.pdfViewerBase.navigationPane) {
            this.pdfViewerBase.navigationPane.closeThumbnailPane();
        }
    }
    /**
     * @private
     * @returns {void}
     */
    createRequestForThumbnails() {
        // eslint-disable-next-line
        const proxy = this;
        proxy.thumbnailLimit = 0;
        for (let i = 0; i < proxy.pdfViewer.pageCount; i++) {
            proxy.renderThumbnailEmptyPage(i);
        }
        if (proxy.pdfViewer.isThumbnailViewOpen) {
            this.isThubmnailOpen = true;
            if (proxy.pdfViewerBase.navigationPane && proxy.pdfViewerBase.navigationPane.sideBarTitle) {
                proxy.pdfViewerBase.navigationPane.sideBarTitle.textContent = proxy.pdfViewer.localeObj.getConstant('Page Thumbnails');
            }
            if (document.getElementById(proxy.pdfViewer.element.id + '_thumbnail_view')) {
                document.getElementById(proxy.pdfViewer.element.id + '_thumbnail_view').style.display = 'flex';
            }
            const bookmarkContent = proxy.pdfViewer.element.querySelector('.e-pv-bookmark-view');
            if (bookmarkContent) {
                bookmarkContent.style.display = 'none';
            }
            proxy.pdfViewerBase.navigationPane.updateViewerContainerOnExpand();
            this.isThubmnailOpen = false;
            proxy.pdfViewerBase.navigationPane.isBookmarkOpen = false;
        }
        this.createRequestForThumbnailImages();
    }
    isThumbnailViewOpen() {
        // eslint-disable-next-line
        const proxy = this;
        if (proxy.pdfViewer.isThumbnailViewOpen) {
            proxy.pdfViewerBase.navigationPane.setThumbnailSelectionIconTheme();
            proxy.pdfViewerBase.navigationPane.isThumbnailOpen = true;
            this.pdfViewerBase.navigationPane.sideBarContentContainer.style.display = 'block';
            if (this.pdfViewer.enableRtl) {
                proxy.pdfViewerBase.viewerContainer.style.right = this.pdfViewerBase.navigationPane.getViewerContainerLeft() + 'px';
            }
            else {
                proxy.pdfViewerBase.viewerContainer.style.left = this.pdfViewerBase.navigationPane.getViewerContainerLeft() + 'px';
            }
            proxy.pdfViewerBase.viewerContainer.style.width = (proxy.pdfViewer.element.clientWidth - this.pdfViewerBase.navigationPane.getViewerContainerLeft() - this.pdfViewerBase.navigationPane.getViewerContainerRight()) + 'px';
            proxy.pdfViewerBase.pageContainer.style.width = proxy.pdfViewerBase.viewerContainer.clientWidth + 'px';
            proxy.pdfViewerBase.updateZoomValue();
        }
    }
    /**
     * Checks if thumbnails have been requested for the given page number.
     *
     * @param {number} pageNumber The page number to check.
     * @returns {boolean} True if thumbnails have been requested, otherwise false.
     */
    thumbnailsRequestedForPage(pageNumber) {
        for (const requestedPage of this.list) {
            if (pageNumber === requestedPage) {
                return true;
            }
        }
        return false;
    }
    /**
     * @param {number} pageNumber - Specify the pageNumber.
     * @returns {void}
     * @private
     */
    updateScrollTopForThumbnail(pageNumber) {
        const step = this.thumbnailRequestsBatch;
        const number = Math.floor((pageNumber + 1) / step) * step;
        const lastNum = this.pdfViewer.thumbnailViewModule.thumbnailLimit;
        const numbers = [number, number - step, number + step, lastNum];
        numbers.forEach((num) => {
            if (num < 0) {
                return;
            }
            if (!this.thumbnailsRequestedForPage(num)) {
                this.renderThumbnailImage(null, num);
            }
        });
    }
    /**
     * @param {number} pageIndex - It describes about the page index
     * @private
     * @returns {void}
     */
    renderThumbnailEmptyPage(pageIndex) {
        if (this.thumbnailView) {
            const pageLink = createElement('a', { id: 'page_' + pageIndex, attrs: { 'aria-label': 'Thumbnail of Page' + (pageIndex + 1), 'tabindex': '-1', 'role': 'link' }, className: 'e-pv-thumbnail-anchor-node' });
            const thumbnail = createElement('div', { id: this.pdfViewer.element.id + '_thumbnail_' + pageIndex, className: 'e-pv-thumbnail e-pv-thumbnail-column' });
            this.thumbnailSelectionRing = createElement('div', { id: this.pdfViewer.element.id + '_thumbnail_Selection_Ring_' + pageIndex, className: 'e-pv-thumbnail-selection-ring' });
            thumbnail.appendChild(this.thumbnailSelectionRing);
            const thumbnailPageNumber = createElement('div', { id: this.pdfViewer.element.id + '_thumbnail_pagenumber_' + pageIndex, className: 'e-pv-thumbnail-number' });
            thumbnailPageNumber.textContent = (pageIndex + 1).toString();
            thumbnail.appendChild(thumbnailPageNumber);
            let height = 180;
            this.thumbnailImage = createElement('img', { id: this.pdfViewer.element.id + '_thumbnail_image_' + pageIndex, className: 'e-pv-thumbnail-image' });
            if (this.pdfViewerBase.pageSize[parseInt(pageIndex.toString(), 10)] &&
                (this.pdfViewerBase.pageSize[parseInt(pageIndex.toString(), 10)].height <
                    this.pdfViewerBase.pageSize[parseInt(pageIndex.toString(), 10)].width)) {
                this.thumbnailImage.style.height = '86px';
                this.thumbnailImage.style.width = '126px';
                thumbnail.style.height = '100px';
                thumbnail.style.width = '140px';
                thumbnailPageNumber.style.left = '18px';
                pageLink.style.marginRight = '41px';
                thumbnail.style.marginLeft = '-5px';
                thumbnail.style.marginRight = '0px';
                height = 140;
            }
            if (pageIndex !== 0) {
                this.thumbnailTop = this.thumbnailPageSize[pageIndex - 1].top + this.thumbnailPageSize[pageIndex - 1].height;
                const thumbnailSize = { height: height, top: this.thumbnailTop };
                this.thumbnailPageSize[parseInt(pageIndex.toString(), 10)] = thumbnailSize;
            }
            else {
                const thumbnailSize = { height: height, top: this.thumbnailTop };
                this.thumbnailPageSize[parseInt(pageIndex.toString(), 10)] = thumbnailSize;
            }
            this.thumbnailSelectionRing.appendChild(this.thumbnailImage);
            pageLink.appendChild(thumbnail);
            this.thumbnailView.appendChild(pageLink);
            this.wireUpEvents();
            if (pageIndex === 0) {
                this.setMouseFocusToFirstPage();
            }
            this.pdfViewerBase.navigationPane.enableThumbnailButton();
        }
    }
    /**
     * @param {ThumbnailView} prox - It describes about the prox
     * @private
     * @returns {void}
     */
    renderViewPortThumbnailImage(prox) {
        const proxy = prox ? prox : this;
        // Removed the condition to skip multiple request for thumbnail image.
        proxy.startIndex = proxy.thumbnailLimit;
        this.list.push(proxy.startIndex);
        if (this.pdfViewerBase.pageSize.length === this.pdfViewerBase.pageCount && !this.isRendered) {
            this.renderDiv();
        }
        proxy.thumbnailLimit = proxy.startIndex + proxy.thumbnailThreshold < proxy.pdfViewer.pageCount ?
            proxy.startIndex + proxy.thumbnailThreshold : proxy.pdfViewer.pageCount;
        if (!this.pdfViewerBase.clientSideRendering) {
            let digitalSignaturePresent = false;
            for (let i = proxy.startIndex; i < proxy.thumbnailLimit; i++) {
                if (proxy.pdfViewerBase.digitalSignaturePresent(i)) {
                    digitalSignaturePresent = true;
                }
            }
            let digitalSignatureList = '';
            if (digitalSignaturePresent) {
                digitalSignatureList = proxy.pdfViewerBase.digitalSignaturePages.toString();
            }
            const jsonObject = { startPage: proxy.startIndex.toString(), endPage: proxy.thumbnailLimit.toString(), sizeX: '99.7', sizeY: '141', hashId: proxy.pdfViewerBase.hashId, action: 'RenderThumbnailImages', elementId: proxy.pdfViewer.element.id, uniqueId: proxy.pdfViewerBase.documentId, digitalSignaturePresent: digitalSignaturePresent, digitalSignaturePageList: digitalSignatureList };
            if (this.pdfViewerBase.jsonDocumentId) {
                jsonObject.documentId = this.pdfViewerBase.jsonDocumentId;
            }
            if (!isNullOrUndefined(this.pdfViewer.pageOrganizer)) {
                const imageSize = this.pdfViewer.pageOrganizer.getImageZoomValue(true);
                jsonObject.imageSize = imageSize;
            }
            this.thumbnailRequestHandler = new AjaxHandler(this.pdfViewer);
            this.thumbnailRequestHandler.url = proxy.pdfViewer.serviceUrl + '/' + proxy.pdfViewer.serverActionSettings.renderThumbnail;
            this.thumbnailRequestHandler.responseType = 'json';
            if ((proxy.startIndex.toString() !== proxy.thumbnailLimit.toString()) && proxy.thumbnailLimit > 0 &&
                !isNullOrUndefined(proxy.pdfViewerBase.hashId)) {
                this.pdfViewerBase.requestCollection.push(this.thumbnailRequestHandler);
                this.thumbnailRequestHandler.send(jsonObject);
            }
            this.thumbnailRequestHandler.onSuccess = function (result) {
                const data = result.data;
                const redirect = proxy.pdfViewerBase.checkRedirection(data);
                if (!redirect) {
                    proxy.updateThumbnailCollection(data);
                }
            };
            this.thumbnailRequestHandler.onFailure = function (result) {
                proxy.pdfViewer.fireAjaxRequestFailed(result.status, result.statusText, proxy.pdfViewer.serverActionSettings.renderThumbnail);
            };
            this.thumbnailRequestHandler.onError = function (result) {
                proxy.pdfViewerBase.openNotificationPopup();
                proxy.pdfViewer.fireAjaxRequestFailed(result.status, result.statusText, proxy.pdfViewer.serverActionSettings.renderThumbnail);
            };
        }
        else {
            for (let count = proxy.startIndex; count < proxy.thumbnailLimit; count++) {
                const currentPageImage = this.getThumbnailImageElement(count);
                const jsonObject = { documentId: proxy.pdfViewerBase.getDocumentId(), hashId: proxy.pdfViewerBase.hashId,
                    elementId: proxy.pdfViewer.element.id, uniqueId: proxy.pdfViewerBase.documentId };
                const isTextNeed = proxy.pdfViewer.textSearch ? true : false;
                const isSkipCharacterBounds = (this.pdfViewer.extractTextOption === ExtractTextOption.None ||
                    this.pdfViewer.extractTextOption === ExtractTextOption.TextOnly) ? true : false;
                const imageSize = proxy.pdfViewer.pageOrganizer ?
                    proxy.pdfViewer.pageOrganizer.getImageZoomValue(true) : 1;
                if ((currentPageImage && currentPageImage.src === '') || (isNullOrUndefined(currentPageImage) && !isNullOrUndefined(this.pdfViewer.pageOrganizer))) {
                    this.pdfViewerBase.pdfViewerRunner.addTask({
                        pageIndex: count,
                        message: 'renderThumbnail',
                        isTextNeed: isTextNeed,
                        jsonObject: jsonObject,
                        isRenderText: isTextNeed,
                        requestType: isTextNeed ? 'pdfTextSearchRequest' : '',
                        isSkipCharacterBounds: isSkipCharacterBounds,
                        imageSize: imageSize
                    }, TaskPriorityLevel.Low);
                }
            }
            this.isThumbnailViewOpen();
        }
    }
    /**
     * @param {any} event - It describes about the event
     * @private
     * @returns {void}
     */
    thumbnailOnMessage(event) {
        if (event.data.message === 'renderThumbnail') {
            const canvas = document.createElement('canvas');
            const { value, width, height, pageIndex, imageSize } = event.data;
            canvas.width = width;
            canvas.height = height;
            const canvasContext = canvas.getContext('2d');
            const imageData = canvasContext.createImageData(width, height);
            imageData.data.set(value);
            canvasContext.putImageData(imageData, 0, 0);
            const imageUrl = canvas.toDataURL();
            this.pdfViewerBase.releaseCanvas(canvas);
            const currentPageImage = this.getThumbnailImageElement(pageIndex);
            if (currentPageImage) {
                currentPageImage.src = imageUrl;
            }
            const data = ({
                thumbnailImage: imageUrl,
                startPage: this.startIndex,
                endPage: this.thumbnailLimit,
                uniqueId: this.pdfViewerBase.documentId,
                pageIndex: pageIndex,
                imageSize: imageSize
            });
            if (!Browser.isDevice || this.pdfViewer.enableDesktopMode) {
                this.updateThumbnailCollection(data);
            }
            else {
                if (!isNullOrUndefined(this.pdfViewer.pageOrganizer)) {
                    this.pdfViewer.pageOrganizer.updatePreviewCollection(data);
                }
            }
        }
    }
    /**
     * @param {any} data - It describes about the data
     * @private
     * @returns {void}
     */
    updateThumbnailCollection(data) {
        if (data) {
            // eslint-disable-next-line
            const proxy = this;
            if (typeof data !== 'object') {
                try {
                    data = JSON.parse(data);
                }
                catch (error) {
                    proxy.pdfViewerBase.onControlError(500, data, proxy.pdfViewer.serverActionSettings.renderThumbnail);
                    data = null;
                }
            }
            if (data && data.uniqueId === proxy.pdfViewerBase.documentId) {
                proxy.pdfViewer.fireAjaxRequestSuccess(proxy.pdfViewer.serverActionSettings.renderThumbnail, data);
                proxy.renderThumbnailImage(data);
            }
        }
    }
    renderDiv(data) {
        if ((this.pdfViewerBase.pageSize.length === this.pdfViewerBase.pageCount || !isNullOrUndefined(data)) && !this.isRendered) {
            for (let i = 100; i < this.pdfViewer.pageCount; i++) {
                const thumbnail = document.getElementById(this.pdfViewer.element.id + '_thumbnail_' + i);
                const pageLink = document.getElementById('page_' + i);
                const thumbnailPageNumber = document.getElementById(this.pdfViewer.element.id + '_thumbnail_pagenumber_' + i);
                const currentPageImage = this.getThumbnailImageElement(i);
                let height = 180;
                if ((this.pdfViewerBase.pageSize[parseInt(i.toString(), 10)] &&
                    (this.pdfViewerBase.pageSize[parseInt(i.toString(), 10)].height <
                        this.pdfViewerBase.pageSize[parseInt(i.toString(), 10)].width)) ||
                    (data && (data.pageRotation[parseInt(i.toString(), 10)] === 1 ||
                        data.pageRotation[parseInt(i.toString(), 10)] === 3))) {
                    currentPageImage.style.height = '86px';
                    currentPageImage.style.width = '126px';
                    thumbnail.style.height = '100px';
                    thumbnail.style.width = '140px';
                    thumbnailPageNumber.style.left = '18px';
                    pageLink.style.marginRight = '41px';
                    thumbnail.style.marginLeft = '-5px';
                    thumbnail.style.marginRight = '0px';
                    height = 140;
                }
                if (this.thumbnailPageSize.length > 0) {
                    this.thumbnailTop = this.thumbnailPageSize[i - 1].top + this.thumbnailPageSize[i - 1].height;
                    const thumbnailSize = { height: height, top: this.thumbnailTop };
                    this.thumbnailPageSize[parseInt(i.toString(), 10)] = thumbnailSize;
                }
            }
            this.isRendered = true;
        }
    }
    /**
     * @param {number} pageNumber - It describes about the page number
     * @private
     * @returns {void}
     */
    gotoThumbnailImage(pageNumber) {
        const shouldScroll = this.checkThumbnailScroll(pageNumber);
        if (this.thumbnailView) {
            const thumbnailChild = this.thumbnailView.children[parseInt(pageNumber.toString(), 10)];
            if (thumbnailChild) {
                const thumbnailDiv = thumbnailChild.children[0];
                let offsetTop;
                if (shouldScroll) {
                    if (this.pdfViewerBase.pageSize.length === this.pdfViewerBase.pageCount && !this.isRendered) {
                        this.renderDiv();
                    }
                    if (thumbnailDiv.offsetTop <= 0) {
                        offsetTop = thumbnailDiv.parentElement.offsetTop + thumbnailDiv.clientTop - this.thumbnailTopMargin;
                    }
                    else {
                        offsetTop = thumbnailDiv.offsetTop + thumbnailDiv.clientTop - this.thumbnailTopMargin;
                    }
                    this.pdfViewerBase.navigationPane.sideBarContent.scrollTop = offsetTop;
                }
                if (!this.isThumbnailClicked) {
                    if (this.previousElement) {
                        this.previousElement.classList.remove('e-pv-thumbnail-selection');
                        this.previousElement.classList.remove('e-pv-thumbnail-focus');
                        this.previousElement.classList.remove('e-pv-thumbnail-hover');
                        this.previousElement.classList.add('e-pv-thumbnail-selection-ring');
                    }
                    this.setFocusStyle(thumbnailDiv, pageNumber);
                }
                this.previousElement = thumbnailDiv.children[0];
            }
        }
    }
    /**
     * Determines if there is a need to request thumbnails based on the current page number.
     *
     * @param {number} currentPageNumber The current page number.
     * @returns {number} The page number to request thumbnails for.
     */
    determineThumbnailsRequest(currentPageNumber) {
        const pageCount = this.pdfViewer.pageCount;
        const batchSize = this.thumbnailRequestsBatch; // Assuming thumbnails are requested in batches of 50
        const numberOfBatches = Math.ceil(pageCount / batchSize);
        if (this.list.length === numberOfBatches) {
            return pageCount;
        }
        for (let i = 0; i < this.list.length; i++) {
            if (currentPageNumber === this.list[parseInt(i.toString(), 10)]) {
                currentPageNumber += batchSize;
                i = -1; // Resetting i to -1 to start from the beginning of the list again
            }
        }
        return currentPageNumber !== undefined && currentPageNumber < pageCount ? currentPageNumber : pageCount;
    }
    checkThumbnailScroll(pageNumber) {
        let shouldScroll = false;
        if (this.thumbnailView) {
            const visibleThumbs = this.getVisibleThumbs();
            const numVisibleThumbs = visibleThumbs.views.length;
            // if the thumbnail isn't currently visible, scroll it into view.
            if (numVisibleThumbs > 0) {
                const visibleFirstPageID = this.getPageNumberFromID(visibleThumbs.first.id);
                // account for only one thumbnail being visible.
                const visibleLastPageID = (numVisibleThumbs > 1 ?
                    this.getPageNumberFromID(visibleThumbs.last.id) : visibleFirstPageID);
                if (pageNumber <= visibleFirstPageID || pageNumber >= visibleLastPageID) {
                    shouldScroll = true;
                }
                else {
                    visibleThumbs.views.some((view) => {
                        const pageID = view.id.split('_');
                        const thumbPageNumber = pageID[pageID.length - 1];
                        if (parseInt(thumbPageNumber, 10) !== pageNumber) {
                            return false;
                        }
                        shouldScroll = view.percent < 100 && (view.view.offsetWidth > view.view.offsetHeight && view.percent < 97);
                        return true;
                    });
                }
            }
        }
        return shouldScroll;
    }
    getPageNumberFromID(pageId) {
        const pageID = pageId.split('_');
        const pageNumber = pageID[pageID.length - 1];
        return parseInt(pageNumber, 10);
    }
    setFocusStyle(thumbnail, pageNumber) {
        if (thumbnail.children[0].id === this.pdfViewer.element.id + '_thumbnail_Selection_Ring_' + pageNumber) {
            this.setMouseFocusStyle(thumbnail.children[0]);
        }
    }
    renderThumbnailImage(data, pageNumber) {
        if (this.thumbnailView && data) {
            if (this.pdfViewerBase.clientSideRendering) {
                this.renderClientThumbnailImage(data);
            }
            else {
                this.renderServerThumbnailImage(data);
            }
        }
        if (!isNullOrUndefined(data) && !isNullOrUndefined(this.pdfViewer.pageOrganizer)) {
            this.pdfViewer.pageOrganizer.getData(data, this.pdfViewerBase.clientSideRendering);
        }
        this.thumbnailLimit = this.determineThumbnailsRequest(!isNullOrUndefined(pageNumber) ? pageNumber : this.thumbnailLimit);
        if (this.thumbnailLimit !== this.pdfViewerBase.pageCount && (this.thumbnailView ||
            !isNullOrUndefined(this.pdfViewer.pageOrganizer))) {
            const isIE = !!document.documentMode;
            if (!isIE) {
                Promise.all([this.createRequestForThumbnailImages()]);
            }
            else {
                this.createRequestForThumbnailImages();
            }
        }
    }
    createRequestForThumbnailImages() {
        // eslint-disable-next-line
        const proxy = this;
        const isIE = !!document.documentMode;
        if (!isIE) {
            return new Promise(function (renderThumbnailImage, reject) {
                proxy.renderViewPortThumbnailImage(proxy);
            });
        }
        else {
            this.renderViewPortThumbnailImage(proxy);
            return null;
        }
    }
    renderServerThumbnailImage(data) {
        const startPage = !isNullOrUndefined(data && (data.startPage)) ? data.startPage : this.startIndex;
        const endPage = !isNullOrUndefined(data && (data.endPage)) ? data.endPage : this.thumbnailLimit;
        for (let i = startPage; i < endPage; i++) {
            this.thumbnailImageRender(i, data);
        }
        this.isThumbnailViewOpen();
    }
    renderClientThumbnailImage(data) {
        const pageIndex = data.pageIndex;
        this.thumbnailImageRender(pageIndex, data);
    }
    thumbnailImageRender(pageIndex, data) {
        if (!isNullOrUndefined(data.pageRotation) && Object.keys(data.pageRotation).length > 0 && !this.isRendered) {
            this.renderDiv(data);
        }
        const thumbnail = document.getElementById(this.pdfViewer.element.id + '_thumbnail_' + pageIndex);
        const pageLink = document.getElementById('page_' + pageIndex);
        const thumbnailPageNumber = document.getElementById(this.pdfViewer.element.id + '_thumbnail_pagenumber_' + pageIndex);
        const currentPageImage = this.getThumbnailImageElement(pageIndex);
        if (!isNullOrUndefined(thumbnail) && !isNullOrUndefined(currentPageImage)) {
            currentPageImage.src = this.pdfViewerBase.clientSideRendering || typeof data.thumbnailImage === 'string' || data.thumbnailImage instanceof String ? data.thumbnailImage : data.thumbnailImage[parseInt(pageIndex.toString(), 10)];
            currentPageImage.alt = this.pdfViewer.element.id + '_thumbnail_page_' + pageIndex;
            pageLink.setAttribute('aria-label', `Thumbnail of Page ${pageIndex + 1}`);
            if (this.pdfViewerBase.pageSize[parseInt(pageIndex.toString(), 10)] &&
                (this.pdfViewerBase.pageSize[parseInt(pageIndex.toString(), 10)].height <
                    this.pdfViewerBase.pageSize[parseInt(pageIndex.toString(), 10)].width)) {
                currentPageImage.style.height = '86px';
                currentPageImage.style.width = '126px';
                thumbnail.style.height = '100px';
                thumbnail.style.width = '140px';
                // pageLink.style.left = '-25px';
                thumbnailPageNumber.style.left = '18px';
                pageLink.style.marginRight = '41px';
                thumbnail.style.marginLeft = '-5px';
                thumbnail.style.marginRight = '0px';
            }
            if (pageIndex === 0) {
                this.pdfViewerBase.navigationPane.enableThumbnailButton();
                this.isThumbnailViewOpen();
            }
        }
    }
    wireUpEvents() {
        if (this.thumbnailSelectionRing) {
            this.thumbnailSelectionRing.addEventListener('click', this.thumbnailClick);
            this.thumbnailImage.addEventListener('keydown', this.thumbnailKeydown);
            this.thumbnailSelectionRing.addEventListener('mouseover', this.thumbnailMouseOver);
            this.thumbnailSelectionRing.addEventListener('mouseleave', this.thumbnailMouseLeave);
        }
    }
    unwireUpEvents() {
        if (this.thumbnailSelectionRing && this.thumbnailImage) {
            this.thumbnailSelectionRing.removeEventListener('click', this.thumbnailClick);
            this.thumbnailImage.removeEventListener('keydown', this.thumbnailKeydown);
            this.thumbnailSelectionRing.removeEventListener('mouseover', this.thumbnailMouseOver);
            this.thumbnailSelectionRing.removeEventListener('mouseleave', this.thumbnailMouseLeave);
        }
    }
    goToThumbnailPage(pageNumber) {
        if (pageNumber > 0 && pageNumber <= this.pdfViewerBase.pageCount && this.pdfViewerBase.currentPageNumber !== pageNumber) {
            this.pdfViewerBase.updateScrollTop(pageNumber - 1);
        }
        else {
            this.isThumbnailClicked = false;
        }
    }
    setSelectionStyle(thumbnailElement) {
        thumbnailElement.classList.remove('e-pv-thumbnail-selection-ring');
        thumbnailElement.classList.remove('e-pv-thumbnail-hover');
        thumbnailElement.classList.remove('e-pv-thumbnail-focus');
        thumbnailElement.classList.add('e-pv-thumbnail-selection');
    }
    setMouseOverStyle(thumbnailElement) {
        if (!thumbnailElement.classList.contains('e-pv-thumbnail-selection')) {
            thumbnailElement.classList.remove('e-pv-thumbnail-selection-ring');
            if (!thumbnailElement.classList.contains('e-pv-thumbnail-focus')) {
                thumbnailElement.classList.add('e-pv-thumbnail-hover');
            }
        }
    }
    setMouseLeaveStyle(thumbnailElement) {
        if (!thumbnailElement.classList.contains('e-pv-thumbnail-selection')) {
            if (!thumbnailElement.classList.contains('e-pv-thumbnail-focus')) {
                thumbnailElement.classList.add('e-pv-thumbnail-selection-ring');
            }
            thumbnailElement.classList.remove('e-pv-thumbnail-hover');
        }
        else {
            if (!thumbnailElement.classList.contains('e-pv-thumbnail-selection')) {
                thumbnailElement.classList.remove('e-pv-thumbnail-selection');
                thumbnailElement.classList.add('e-pv-thumbnail-focus');
            }
        }
    }
    setMouseFocusStyle(thumbnailElement) {
        thumbnailElement.classList.remove('e-pv-thumbnail-selection');
        thumbnailElement.classList.remove('e-pv-thumbnail-hover');
        thumbnailElement.classList.add('e-pv-thumbnail-focus');
    }
    setMouseFocusToFirstPage() {
        const thumbnailChild = this.thumbnailView.children[0];
        if (thumbnailChild) {
            const thumbnailDiv = thumbnailChild.children[0].children[0];
            this.setMouseFocusStyle(thumbnailDiv);
            this.previousElement = thumbnailDiv;
        }
    }
    /**
     * @private
     * @returns {void}
     */
    clear() {
        this.startIndex = 0;
        this.thumbnailLimit = 0;
        this.list = [];
        this.thumbnailPageSize = [];
        this.thumbnailTop = 0;
        this.isRendered = false;
        if (this.pdfViewerBase.navigationPane) {
            if (this.pdfViewerBase.navigationPane.sideBarContentContainer) {
                this.pdfViewerBase.navigationPane.sideBarContentContainer.style.display = 'block';
                this.pdfViewerBase.navigationPane.sideBarContent.scrollTop = 0;
                this.pdfViewerBase.navigationPane.sideBarContentContainer.style.display = 'none';
            }
        }
        if (this.thumbnailView) {
            while (this.thumbnailView.hasChildNodes()) {
                this.thumbnailView.removeChild(this.thumbnailView.lastChild);
            }
        }
        if (this.pdfViewerBase.navigationPane) {
            this.pdfViewerBase.navigationPane.resetThumbnailView();
        }
        if (this.thumbnailRequestHandler) {
            this.thumbnailRequestHandler.clear();
        }
        this.unwireUpEvents();
    }
    getVisibleThumbs() {
        return this.getVisibleElements(this.pdfViewerBase.navigationPane.sideBarContent, this.thumbnailView.children);
    }
    getVisibleElements(scrollElement, thumbnailViewChildren) {
        const top = scrollElement.scrollTop;
        const bottom = top + scrollElement.clientHeight;
        const left = scrollElement.scrollLeft;
        const right = left + scrollElement.clientWidth;
        /**
         * @param {HTMLElement} thumbnailViewChildrenElement - It describes about the thumbnail view children element
         * @returns {boolean} - boolean
         */
        function isThumbnailElementBottomAfterViewTop(thumbnailViewChildrenElement) {
            const elementBottom = thumbnailViewChildrenElement.offsetTop + thumbnailViewChildrenElement.clientTop + thumbnailViewChildrenElement.clientHeight;
            return elementBottom > top;
        }
        const visible = [];
        let thumbnailView;
        let element;
        let currentHeight;
        let viewHeight;
        let viewBottom;
        let hiddenHeight;
        let currentWidth;
        let viewWidth;
        let viewRight;
        let hiddenWidth;
        let percentVisible;
        let firstVisibleElementInd = thumbnailViewChildren.length === 0 ? 0 :
            this.binarySearchFirstItem(thumbnailViewChildren, isThumbnailElementBottomAfterViewTop);
        if (thumbnailViewChildren.length > 0) {
            firstVisibleElementInd =
                this.backtrackBeforeAllVisibleElements(firstVisibleElementInd, thumbnailViewChildren, top);
        }
        let lastEdge = -1;
        for (let i = firstVisibleElementInd, ii = thumbnailViewChildren.length; i < ii; i++) {
            thumbnailView = this.getThumbnailElement(i);
            element = thumbnailView;
            currentWidth = element.offsetLeft + element.clientLeft;
            currentHeight = element.offsetTop + element.clientTop;
            viewWidth = element.clientWidth;
            viewHeight = element.clientHeight;
            viewRight = currentWidth + viewWidth;
            viewBottom = currentHeight + viewHeight;
            if (lastEdge === -1) {
                if (viewBottom >= bottom) {
                    lastEdge = viewBottom;
                }
            }
            else if (currentHeight > lastEdge) {
                break;
            }
            if (viewBottom <= top || currentHeight >= bottom ||
                viewRight <= left || currentWidth >= right) {
                continue;
            }
            hiddenHeight = Math.max(0, top - currentHeight) +
                Math.max(0, viewBottom - bottom);
            hiddenWidth = Math.max(0, left - currentWidth) +
                Math.max(0, viewRight - right);
            percentVisible = ((viewHeight - hiddenHeight) * (viewWidth - hiddenWidth) * 100 / viewHeight / viewWidth) | 0;
            visible.push({
                id: thumbnailView.id,
                x: currentWidth,
                y: currentHeight,
                view: thumbnailView,
                percent: percentVisible
            });
        }
        const first = visible[0];
        const last = visible[visible.length - 1];
        return { first: first, last: last, views: visible };
    }
    binarySearchFirstItem(items, condition) {
        let minIndex = 0;
        let maxIndex = items.length - 1;
        if (items.length === 0 || !condition(this.getThumbnailElement(maxIndex))) {
            return items.length - 1;
        }
        if (condition(this.getThumbnailElement(minIndex))) {
            return minIndex;
        }
        while (minIndex < maxIndex) {
            const currentIndex = (minIndex + maxIndex) >> 1;
            if (condition(this.getThumbnailElement(currentIndex))) {
                maxIndex = currentIndex;
            }
            else {
                minIndex = currentIndex + 1;
            }
        }
        return minIndex; /* === maxIndex */
    }
    backtrackBeforeAllVisibleElements(index, views, top) {
        if (index < 2) {
            return index;
        }
        let thumbnailElement = this.getThumbnailElement(index);
        let pageTop = thumbnailElement.offsetTop + thumbnailElement.clientTop;
        if (pageTop >= top) {
            thumbnailElement = this.getThumbnailElement(index - 1);
            pageTop = thumbnailElement.offsetTop + thumbnailElement.clientTop;
        }
        for (let i = index - 2; i >= 0; --i) {
            thumbnailElement = this.getThumbnailElement(i);
            if (thumbnailElement.offsetTop + thumbnailElement.clientTop + thumbnailElement.clientHeight <= pageTop) {
                break;
            }
            index = i;
        }
        return index;
    }
    /**
     * @param {number} index - It describes about the index value
     * @private
     * @returns {HTMLElement} - html element
     */
    getThumbnailElement(index) {
        const thumbnailChild = this.thumbnailView.children[parseInt(index.toString(), 10)];
        return thumbnailChild.children[0];
    }
    /**
     * @param {number} index - It describes about the index value
     * @private
     * @returns {HTMLElement} - html element
     */
    getThumbnailLinkElement(index) {
        const thumbnailChild = this.thumbnailView.children[parseInt(index.toString(), 10)];
        return thumbnailChild;
    }
    /**
     * @param {number} index - It describes about the index value
     * @private
     * @returns {HTMLImageElement} - html image element
     */
    getThumbnailImageElement(index) {
        if (isNullOrUndefined(this.thumbnailView)) {
            return null;
        }
        const thumbnailChild = this.thumbnailView.children[parseInt(index.toString(), 10)];
        if (thumbnailChild) {
            return thumbnailChild.children[0].children[0].children[0];
        }
        else {
            return null;
        }
    }
    /**
     * @private
     * @returns {void}
     */
    destroy() {
        this.clear();
    }
    /**
     * @private
     * @returns {string} - string
     */
    getModuleName() {
        return 'ThumbnailView';
    }
}
